"use client";

import React from "react";
import { CalendarClock, CircleDollarSign, Handshake, ShieldCheck, ArrowRight, ClipboardCheck, PhoneCall, HardHat, ChevronRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const benefits = [{
  title: "Predictable scheduling",
  description: "Book scaffold slots ahead of time so your roofers never arrive to an unready site or wait on uninspected handovers.",
  icon: CalendarClock,
  highlight: "Priority calendar slots"
}, {
  title: "Direct contractor rates",
  description: "Agreed schedule of rates and clear hire terms give you confident job estimates without unexpected variation charges.",
  icon: CircleDollarSign,
  highlight: "Transparent job pricing"
}, {
  title: "Reliable coordination",
  description: "Direct contact with our supervisors ensures quick turnarounds for adaptations, chimney lifts, and phased strikes.",
  icon: Handshake,
  highlight: "Single point of contact"
}, {
  title: "Compliance & handovers",
  description: "Full TG20 compliance, NASC standard scaffolding, and digital handover certificates ready for your site audit.",
  icon: ShieldCheck,
  highlight: "NASC & CHAS accredited"
}];
const enquirySteps = [{
  step: "01",
  title: "Share your schedule",
  description: "Send over your upcoming roofing pipeline, drawings, or site addresses with required start dates.",
  icon: ClipboardCheck
}, {
  step: "02",
  title: "Agree framework terms",
  description: "We supply transparent schedule-of-rate pricing or fixed tenders matched to your standard roof types.",
  icon: PhoneCall
}, {
  step: "03",
  title: "Book & erect on schedule",
  description: "Our teams erect, tag, and hand over safe working platforms exactly when your roofers need them.",
  icon: HardHat
}];
export default function RoofingPartnership() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="roofingpartnershipbenefits" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 bg-primary/5">
            Contractor Partnerships
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold font-default text-foreground tracking-tight">
            Reliable scaffold access for busy roofing contractors
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Keep your roofing teams moving with reserved programme dates, transparent contractor pricing, and direct communication on every plot.
          </p>
        </div>

        {/* Benefits Grid */}
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }}>
          {benefits.map((benefit, index) => {
          const IconComponent = benefit.icon;
          return <motion.div key={benefit.title} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                      <IconComponent className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </CardDescription>
                    <div className="pt-2">
                      <span className="inline-flex items-center text-xs font-semibold text-primary bg-primary/5 px-2.5 py-1 rounded-md">
                        {benefit.highlight}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* How to Enquire / Workflow Card */}
        <div className="bg-muted/40 border border-border rounded-2xl p-6 sm:p-10 lg:p-12 mb-16">
          <div className="max-w-2xl mb-8">
            <Badge variant="secondary" className="mb-2 text-xs font-medium">
              Simple Onboarding
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
              How to set up your contractor arrangement
            </h3>
            <p className="mt-2 text-muted-foreground text-sm sm:text-base">
              Whether you need regular scaffolds across the Home Counties or a single major reroof access, getting started is straightforward.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {enquirySteps.map((step, idx) => {
            const StepIcon = step.icon;
            return <div key={step.step} data-index={idx} className="bg-card border border-border rounded-xl p-6 relative flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-mono font-bold px-2 py-1 bg-secondary text-secondary-foreground rounded">
                        STEP {step.step}
                      </span>
                      <StepIcon className="size-5 text-muted-foreground" />
                    </div>
                    <h4 className="text-lg font-semibold font-default text-foreground mb-2">
                      {step.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* CTA & Resource Navigation Banner */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 sm:p-10 lg:p-12 shadow-md">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl space-y-3">
              <h3 className="text-2xl sm:text-3xl font-bold font-default tracking-tight">
                Ready to plan your next roofing projects?
              </h3>
              <p className="text-secondary-foreground/80 text-sm sm:text-base leading-relaxed">
                Speak directly with Pete Wood and the operational team to establish contractor rates and reserve your upcoming programme dates.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactRequestQuotePage">
                  Discuss a Roofing Partnership
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium bg-background text-foreground hover:bg-accent border-border">
                <Link to="/ContactRequestQuotePage">
                  Request a Contractor Quote
                </Link>
              </Button>
            </div>
          </div>

          {/* Quick Sub-Links */}
          <div className="mt-8 pt-6 border-t border-secondary-foreground/10 flex flex-wrap items-center justify-between gap-4 text-xs sm:text-sm text-secondary-foreground/75">
            <div className="flex items-center gap-2">
              <span>Related services & past work:</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/RoofingScaffoldingPage" className="inline-flex items-center gap-1 hover:text-secondary-foreground transition-colors underline-offset-4 hover:underline">
                Roofing Scaffolding services
                <ChevronRight className="size-3.5" />
              </Link>
              <Link to="/ProjectsCaseStudiesPage" className="inline-flex items-center gap-1 hover:text-secondary-foreground transition-colors underline-offset-4 hover:underline">
                View completed Projects
                <ChevronRight className="size-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}